import { memo, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { Box, Stack, Typography } from "@mui/material";
import { IShipmentByType } from "../../../interfaces/dashboard";
import { formatNumber, formatRate } from "../../../utils/metricsFormatters";
import ChartContainer from "../ChartContainer";
import "../chartSetup";

const COLORS = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2"];

const SERVICE_LABELS: Record<string, string> = {
  DOOR_TO_DOOR: "Puerta a puerta",
  REVERSE_LOGISTICS: "Log. inversa",
  PICKUP: "Punto de retiro",
  REVERSE_LOGISTICS_PICKUP: "Log. inversa (retiro)",
};

// Figma reemplaza la leyenda nativa de Chart.js por una lista debajo del
// gráfico con nombre + cantidad + porcentaje por servicio.
const OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
};

interface IShipmentsByTypeProps {
  data: IShipmentByType[];
}

const ShipmentsByType = ({ data }: IShipmentsByTypeProps) => {
  const total = useMemo(
    () => data.reduce((sum, d) => sum + d.count, 0),
    [data],
  );

  const chartData = useMemo(
    () => ({
      labels: data.map((d) => SERVICE_LABELS[d.service] || d.service),
      datasets: [
        {
          data: data.map((d) => d.count),
          backgroundColor: data.map((_, i) => COLORS[i % COLORS.length]),
          borderWidth: 1,
        },
      ],
    }),
    [data],
  );

  return (
    <Box>
      <ChartContainer height={180}>
        <Doughnut data={chartData} options={OPTIONS} />
      </ChartContainer>
      <Stack spacing={1} mt={2}>
        {data.map((item, index) => (
          <Box
            key={item.service}
            display="flex"
            alignItems="center"
            gap={1}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: COLORS[index % COLORS.length],
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" color="text.primary" sx={{ flex: 1 }}>
              {SERVICE_LABELS[item.service] || item.service}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatNumber(item.count)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 48, textAlign: "right" }}
            >
              {total > 0 ? formatRate((item.count / total) * 100) : "—"}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default memo(ShipmentsByType);
